import dayjs, { Dayjs } from 'dayjs';

import {
    AccountablePlanData,
    AccountablePlanStatuses,
    ParsedAccountablePlanData,
    UseAccountablePlansResponse,
} from 'services/bookkeeping/queries/useAccountablePlans/useAccountablePlans.interface';

export const V2_RELEASE_DATE = dayjs('2025-08-01');

/**
 * Safely parse a string to number, returning 0 if invalid
 */
function safeParseFloat(value: string | null | undefined): number {
    const parsed = parseFloat(value || '');
    return Number.isNaN(parsed) ? 0 : parsed;
}

export function castPlanValues(plans: UseAccountablePlansResponse): ParsedAccountablePlanData[] {
    return plans.map((plan) => ({
        ...plan,
        square_footage_home: safeParseFloat(plan.square_footage_home),
        square_footage_office: safeParseFloat(plan.square_footage_office),
        total_reimbursements: safeParseFloat(plan.total_reimbursements),
        cost_security: safeParseFloat(plan.cost_security),
        cost_cleaning: safeParseFloat(plan.cost_cleaning),
        cost_utilities: safeParseFloat(plan.cost_utilities),
        cost_HOA: safeParseFloat(plan.cost_HOA),
        cost_home_insurance: safeParseFloat(plan.cost_home_insurance),
        cost_property_taxes: safeParseFloat(plan.cost_property_taxes),
        cost_mortgage_interest: safeParseFloat(plan.cost_mortgage_interest),
        cost_rent: safeParseFloat(plan.cost_rent),
        cost_rent_insurance: safeParseFloat(plan.cost_rent_insurance),
        miles_driven_total: safeParseFloat(plan.miles_driven_total),
        miles_driven_business: safeParseFloat(plan.miles_driven_business),
        internet_phone_business_percent: safeParseFloat(plan.internet_phone_business_percent),
        cost_internet: safeParseFloat(plan.cost_internet),
        cost_phone: safeParseFloat(plan.cost_phone),
    }));
}

export function undoCastPlanValues(plan: ParsedAccountablePlanData): AccountablePlanData {
    return {
        ...plan,
        square_footage_home: plan.square_footage_home?.toString() || '',
        square_footage_office: plan.square_footage_office?.toString() || '',
        total_reimbursements: plan.total_reimbursements?.toString() || '',
        cost_security: plan.cost_security?.toString() || '',
        cost_cleaning: plan.cost_cleaning?.toString() || '',
        cost_utilities: plan.cost_utilities?.toString() || '',
        cost_HOA: plan.cost_HOA?.toString() || '',
        cost_home_insurance: plan.cost_home_insurance?.toString() || '',
        cost_property_taxes: plan.cost_property_taxes?.toString() || '',
        cost_mortgage_interest: plan.cost_mortgage_interest?.toString() || '',
        cost_rent: plan.cost_rent?.toString() || '',
        cost_rent_insurance: plan.cost_rent_insurance?.toString() || '',
        miles_driven_total: plan.miles_driven_total?.toString() || '',
        miles_driven_business: plan.miles_driven_business?.toString() || '',
        internet_phone_business_percent: plan.internet_phone_business_percent?.toString() || '',
        cost_internet: plan.cost_internet?.toString() || '',
        cost_phone: plan.cost_phone?.toString() || '',
    };
}

export function generatePrefilledAccountablePlan(
    selectedPeriod: Dayjs,
    previousPlan: ParsedAccountablePlanData
): ParsedAccountablePlanData {
    return {
        ...previousPlan,
        month: selectedPeriod.month() + 1,
        year: selectedPeriod.year(),
        miles_driven_business: 0, // Miles should not be prefilled
        status: AccountablePlanStatuses.IN_PROGRESS,
    };
}

export function generateEmptyAccountablePlan(selectedPeriod: Dayjs, memberId: number) {
    return {
        month: selectedPeriod.month() + 1,
        year: selectedPeriod.year(),
        member: memberId,
        internet_phone_business_percent: 70,
        is_renting: false,
        square_footage_home: 0,
        square_footage_office: 0,
        cost_cleaning: 0,
        cost_utilities: 0,
        cost_HOA: 0,
        cost_home_insurance: 0,
        cost_property_taxes: 0,
        cost_mortgage_interest: 0,
        cost_rent: 0,
        cost_rent_insurance: 0,
        cost_internet: 0,
        cost_phone: 0,
        cost_security: 0,
        miles_driven_total: 0,
        miles_driven_business: 0,
        total_reimbursements: 0,
        status: AccountablePlanStatuses.IN_PROGRESS,
    } as ParsedAccountablePlanData;
}

export function checkIsPeriodAfterV2Release(selectedPeriod: Dayjs) {
    return selectedPeriod.isAfter(V2_RELEASE_DATE);
}
